import React, { useState, useEffect, useRef } from "react";
import { useUser } from "../contexts/UserContext";
import { Resources } from "../utils/resources";
import UserGoalInput from "./UserGoalInput";
import Loader from "./Loader";
import { Button } from "./Button";
import thumbsUpIcon from "../assets/icons/thumbs-up-icon.svg";
import thumbsDownIcon from "../assets/icons/thumbs-down-icon.svg";

interface ReviewActionsContentProps {
  onLoadingStateChange?: (isLoading: boolean) => void;
  onClose?: () => void;
}

const ReviewActionsContent: React.FC<ReviewActionsContentProps> = ({
  onLoadingStateChange,
  onClose,
}) => {
  const { userGoal } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isThumbsUpSelected, setIsThumbsUpSelected] = useState(false);
  const [isThumbsDownSelected, setIsThumbsDownSelected] = useState(false);
  const [showTextBox, setShowTextBox] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isRetailPurchaseOffered, setIsRetailPurchaseOffered] = useState(false);
  const [isLoanOffered, setIsLoanOffered] = useState(false);
  const [isImmediateNeed, setIsImmediateNeed] = useState(false);
  const [needSignPosting, setNeedSignPosting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userGoal) return;

    setIsRetailPurchaseOffered(
      userGoal.goal_actions.some(
        (action) => action.goal_action === "retailDiscounts",
      ),
    );
    setIsLoanOffered(
      userGoal.goal_actions.some((action) => action.goal_action === "loan"),
    );
    setIsImmediateNeed(userGoal.response.timeframe === "immediate");
    setNeedSignPosting(userGoal.response.risk_score >= Resources.riskThreshold);
  }, [userGoal]);

  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange(isLoading);
    }
  }, [isLoading, onLoadingStateChange]);

  const toggleTextBox = (show: boolean) => {
    setShowTextBox(show);
    setIsThumbsUpSelected(false);
    setIsThumbsDownSelected(show);

    if (show) {
      // Scroll to bottom of the popup content
      setTimeout(() => {
        const popupContent = document.querySelector(
          ".absolute.inset-0.pb-6.px-1",
        );
        if (popupContent) {
          popupContent.scrollTo({
            top: popupContent.scrollHeight,
            behavior: "smooth",
          });
        }
        // Also scroll the text box into view as fallback
        if (scrollRef.current) {
          scrollRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  };

  const generateBulletPoints = () => {
    const bulletPoints: string[] = [];

    if (needSignPosting) {
      bulletPoints.push(
        "Your current situation means you're looking for help and support.",
      );
      bulletPoints.push("You're unsure where to get this support from.");
    }

    if (isImmediateNeed) {
      bulletPoints.push(
        "This is something that needs immediate attention – it can't wait.",
      );
    } else {
      bulletPoints.push(
        "This doesn't need immediate attention, but you're likely to need it soon.",
      );
    }

    if (isRetailPurchaseOffered) {
      bulletPoints.push("You need to make a one-off purchase.");
    }

    if (isLoanOffered) {
      bulletPoints.push(
        "You may need to borrow money to bridge a gap in your finances.",
      );
    }

    return bulletPoints;
  };

  if (!userGoal) {
    return (
      <div className="text-center py-8">
        <p>Unable to load review information.</p>
      </div>
    );
  }

  return (
    <div
      className={`relative transition-opacity duration-300 ${
        isClosing ? "opacity-0" : "opacity-100"
      }`}
    >
      <div className="py-3 border-b">
        <p className="text-lg font-semibold">
          You told us: "
          <span className="font-normal">{userGoal.goal_statement}</span>"
        </p>
        {/* <div className="bg-white border border-l-8 border-primary shadow-lg p-4 mb-5">
          <p className="text-base mask-data">{userGoal.goal_statement}</p>
        </div> */}
      </div>

      <div className="py-3 border-b">
        <h3 className="text-lg font-semibold mb-2">
          From this, we understand that:
        </h3>
        <ul className="list-disc pl-6 space-y-2">
          {generateBulletPoints().map((point, index) => (
            <li key={index} className="text-base">
              {point}
            </li>
          ))}
        </ul>
      </div>

      <div className="py-3 border-b text-center">
        <p className="font-bold mb-4">Was our understanding correct?</p>
        <div className="flex justify-center gap-4">
          <div data-testid="thumbs-up">
            <Button
              text="Yes"
              icon={thumbsUpIcon}
              iconAlt="Thumbs up"
              onClick={() => {
                setIsThumbsUpSelected(true);
                setIsThumbsDownSelected(false);
                setShowTextBox(false);
                // Start fade out after showing selection for longer
                setTimeout(() => {
                  setIsClosing(true);
                  // Close after fade animation completes
                  setTimeout(() => {
                    if (onClose) {
                      onClose();
                    }
                  }, 200); // Time for fade animation
                }, 600); // Increased from 500ms to 1500ms
              }}
              variant="light"
              className={
                isThumbsUpSelected ? "!bg-grey-40 !border-2 !border-green" : ""
              }
            />
          </div>
          <div data-testid="thumbs-down">
            <Button
              text="No"
              icon={thumbsDownIcon}
              iconAlt="Thumbs down"
              onClick={() => toggleTextBox(true)}
              variant="light"
              className={
                isThumbsDownSelected ? "!bg-grey-40 !border-2 !border-red" : ""
              }
            />
          </div>
        </div>
      </div>

      {showTextBox && (
        <div ref={scrollRef} className="relative p-2">
          <p className="text-base py-3">
            Can you try describing your financial problem or goal again?
          </p>
          <UserGoalInput
            onLoadingStateChanged={setIsLoading}
            initialPrompt={`Remember, the more specific and detailed you are, the better ${Resources.appName} may be able to help.`}
            isFeedback={true}
            parentGoalId={userGoal.user_goal_id}
            onGoalProcessed={onClose}
          />
          {isLoading && (
            <div className="absolute inset-0 bg-white flex items-center justify-center rounded-20 z-50">
              <Loader
                message={`${Resources.appName} is working to solve your problem...`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewActionsContent;
